/**
 * Cookie Consent Store for Alpine.js
 *
 * Manages two first-party cookies, `analytics_consent` and
 * `marketing_consent` (granted|denied). Analytics consent dynamically
 * injects/removes Google Analytics (gtag.js); both categories are relayed
 * to Google via Consent Mode v2 (`gtag('consent','update',…)`) so tags
 * loaded through GTM respect the choice. Seomatic's built-in gtag
 * rendering is disabled in the layout — we own the script lifecycle
 * entirely. The matching `gtag('consent','default',…)` runs inline in the
 * layout <head>, before GTM, reading the same cookies.
 *
 * State is mirrored to localStorage via Alpine $persist so the banner is
 * not re-shown to returning visitors, and to real cookies read by the
 * inline consent-default script on every page load.
 */

const ANALYTICS_COOKIE = "analytics_consent";
const MARKETING_COOKIE = "marketing_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 6 months
const GTAG_ATTR = "data-consent-gtag";

function setConsentCookie(name, value) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function expireGaCookies() {
  const host = location.hostname;
  const domains = [host, `.${host}`, ""];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (!name) return;
    if (name === "_ga" || name.startsWith("_ga_") || name === "_gid" || name === "_gat") {
      domains.forEach((d) => {
        const domainPart = d ? `; domain=${d}` : "";
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}`;
      });
    }
  });
}

function loadGtag(gaId) {
  if (!gaId) return;
  if (document.querySelector(`script[${GTAG_ATTR}]`)) return;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  s.setAttribute(GTAG_ATTR, "");
  document.head.appendChild(s);

  gtag("js", new Date());
  gtag("config", gaId, { anonymize_ip: true });
}

function unloadGtag(gaId) {
  if (gaId) {
    window[`ga-disable-${gaId}`] = true;
  }
  document
    .querySelectorAll(`script[${GTAG_ATTR}]`)
    .forEach((node) => node.remove());
  expireGaCookies();
}

/** Relay the current consent state to Google (Consent Mode v2). */
function pushConsentUpdate(analytics, marketing) {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
  window.gtag("consent", "update", {
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
  });
}

export default function registerConsentStore(Alpine) {
  Alpine.store("consent", {
    cookieChoice: Alpine.$persist(false).as("_x_cookieChoice"),
    gaAccepted: Alpine.$persist(false).as("_x_gaAccepted"),
    marketingAccepted: Alpine.$persist(false).as("_x_marketingAccepted"),
    decidedAt: Alpine.$persist(null).as("_x_consentDecidedAt"),

    /** Apply a consent decision: persist state, write cookies, relay to Google, load/unload gtag. */
    apply(gaId, gaAccepted, marketingAccepted) {
      this.gaAccepted = !!gaAccepted;
      this.marketingAccepted = !!marketingAccepted;
      this.cookieChoice = true;
      this.decidedAt = new Date().toISOString();
      setConsentCookie(ANALYTICS_COOKIE, this.gaAccepted ? "granted" : "denied");
      setConsentCookie(MARKETING_COOKIE, this.marketingAccepted ? "granted" : "denied");
      pushConsentUpdate(this.gaAccepted, this.marketingAccepted);
      window.dataLayer.push({ event: "consent_update" });
      if (this.gaAccepted) loadGtag(gaId);
      else unloadGtag(gaId);
    },

    /** Re-apply current state on page load (for returning visitors). */
    hydrate(gaId) {
      if (!this.cookieChoice) return;
      setConsentCookie(ANALYTICS_COOKIE, this.gaAccepted ? "granted" : "denied");
      setConsentCookie(MARKETING_COOKIE, this.marketingAccepted ? "granted" : "denied");
      // Covers localStorage outliving the cookies: the inline default then
      // reads "denied", so correct it. No `consent_update` event here — that
      // is reserved for explicit user decisions (GTM trigger contract).
      pushConsentUpdate(this.gaAccepted, this.marketingAccepted);
      if (this.gaAccepted) loadGtag(gaId);
    },
  });
}
